import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Building2 } from 'lucide-react';
import type { CompanyEngagement } from '../../../lib/api';
import {
  COMPANY_TYPE_LABEL,
  ENGAGEMENT_LEVEL_LABEL,
  ENGAGEMENT_LEVEL_STYLE,
} from '../platformAnalyticsConfig';
import { ChartEmptyState } from './ChartEmptyState';

const BAR_COLORS = ['#6366f1', '#818cf8', '#a5b4fc', '#10b981', '#34d399'];

interface OrganisationsTabProps {
  companies: CompanyEngagement[];
}

export function OrganisationsTab({ companies }: OrganisationsTabProps) {
  const chartData = [...companies]
    .sort((a, b) => b.active_users - a.active_users)
    .slice(0, 12)
    .map((c) => ({
      name: truncateName(c.company_name, 18),
      fullName: c.company_name,
      people: c.active_users,
      areas: c.unique_features,
      level: c.engagement_level || 'inactive',
    }));

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 className="h-5 w-5" />
            Most active organisations
          </CardTitle>
          <CardDescription>Companies ranked by number of people using the portal.</CardDescription>
        </CardHeader>
        <CardContent>
          {chartData.length === 0 ? (
            <ChartEmptyState />
          ) : (
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 56 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={70} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={32} />
                <Tooltip
                  formatter={(v: number) => [v, 'People']}
                  labelFormatter={(_, payload) => {
                    const row = payload?.[0]?.payload as { fullName?: string } | undefined;
                    return row?.fullName || '';
                  }}
                />
                <Bar dataKey="people" radius={[4, 4, 0, 0]}>
                  {chartData.map((_, i) => (
                    <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">All organisations</CardTitle>
          <CardDescription>Engagement level reflects breadth and frequency of portal use.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="pb-2 font-medium">Organisation</th>
                  <th className="pb-2 font-medium">Type</th>
                  <th className="pb-2 font-medium text-right">People</th>
                  <th className="pb-2 font-medium text-right">Areas visited</th>
                  <th className="pb-2 font-medium text-right">Engagement</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((company) => {
                  const level = company.engagement_level || 'inactive';
                  return (
                    <tr key={company.company_id} className="border-b last:border-0 hover:bg-muted/50">
                      <td className="py-2.5 font-medium">{company.company_name}</td>
                      <td className="py-2.5 text-xs text-muted-foreground">
                        {COMPANY_TYPE_LABEL[company.company_type] || company.company_type}
                      </td>
                      <td className="py-2.5 text-right">{company.active_users}</td>
                      <td className="py-2.5 text-right">{company.unique_features}</td>
                      <td className="py-2.5 text-right">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ENGAGEMENT_LEVEL_STYLE[level]}`}>
                          {ENGAGEMENT_LEVEL_LABEL[level]}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {companies.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-muted-foreground py-10">
                      No organisation activity for this period.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function truncateName(name: string, max: number): string {
  return name.length > max ? `${name.slice(0, max - 1)}…` : name;
}
